import BetaModal from '@/components/admin/courses/BetaModal';
import EnableEditModal from '@/components/admin/courses/EnableEditModal';
import PublishBetaModal from '@/components/admin/courses/PublishBetaModal';
import PublishEditModal from '@/components/admin/courses/PublishEditModal';
import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import HeadTitle from '@/components/ui/custom/HeadTitle';
import Heading3 from '@/components/ui/custom/Heading3';
import ActionDetailCard from '@/components/ui/custom/buttons/ActionDetailCard';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import InfiniteSearchInput from '@/components/ui/custom/dataInput/InfiniteSearchInput';
import ModalBank from '@/components/ui/custom/modals/ModalBank/ModalBank';
import ModalEmptyActivity from '@/components/ui/custom/modals/ModalEmptyActivity';
import CourseStatus from '@/components/_deprecated/courses/CourseStatus';
import LegalInformationCourseCollapse from '@/components/_deprecated/courses/LegalInformationCourseCollapse';
import { BranchIcon } from '@/components/icons';
import AddExtraLessonModal from '@/components/_deprecated/lessons/extra-lessons/AddExtraLessonModal';
import ListLessonsFactory from '@/components/_deprecated/lessons/factory/ListLessonsFactory';
import Layout from '@/components/template/Layout';
import { REQUEST_STALE_TIME_1_DAY_IN_MS } from '@/constants';
import useAuth from '@/data/hook/useAuth';
import useCourseEditing from '@/data/hook/useCourseEditing';
import { deleteActivity } from '@/data/services/_deprecated/activityServices';
import { ListCoursePathFilters } from '@/data/services/coursePathServices';
import {
  getEmptyActivities,
  listLessonsByCourseSlug,
} from '@/data/services/lessonServices';
import {
  coursePathsQueryKeys,
  klassesQueryKeys,
  scheduledLessonsQueryKeys,
} from '@/data/services/querykeys';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import { isContentCreator, isUnitAdmin, isUnitStaff } from '@/functions/auth';
import { EmptyActivityWithLesson } from '@/models/_deprecated/Activity';
import CoursePath from '@/models/CoursePath';
import { ApiError } from '@/models/Errors';
import Klass, { KlassStatusEnum } from '@/models/Klass';
import Lesson from '@/models/Lesson';
import { UserTypeEnum } from '@/models/User';
import alert from '@/utils/shared/alert';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import { translateKlassName } from '@/utils/translateKlassName';
import {
  ChevronLeftIcon,
  CogIcon,
  RectangleStackIcon,
  PencilIcon,
  PlusIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

export default function KlassPageAdmin() {
  type activeRequestType = 'none' | 'publish' | 'edit' | 'version';
  type modalsTypes =
    | 'none'
    | 'beta'
    | 'beta-publish'
    | 'edit-publish'
    | 'edit'
    | 'activity';
  const { t } = useTranslation('translation', {
    keyPrefix: 'coursesView.admin',
  });
  const { t: tErrors } = useTranslation('translation', {
    keyPrefix: 'erros',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { user: authUser } = useAuth();

  const { slugCourseName = '', klassId } = useParams();
  const [modalOpen, setModalOpen] = useState<modalsTypes>('none');
  const [activeRequest, setActiveRequest] = useState<activeRequestType>('none');
  const [reload, setReload] = useState(false);
  const [hasInvalidLessons, setHasInvalidLessons] = useState(false);
  const [emptyActivities, setEmptyActivities] = useState<
    EmptyActivityWithLesson[]
  >([]);

  const [isLoadingDeleteActivities, setIsLoadingDeleteActivities] =
    useState(false);

  const navigate = useNavigate();
  const {
    allowEditingMode,
    allowVersioningMode,
    courseToPublish,
    handleKlassChange,
    setChanged,
  } = useCourseEditing();

  const { data: coursePath, isError } = useQuery({
    refetchOnWindowFocus: false,
    enabled: !!slugCourseName,
    ...coursePathsQueryKeys.get(slugCourseName),
  });

  const [selectedCoursePath, setSelectedCoursePath] = useState<CoursePath>();

  useEffect(() => {
    setSelectedCoursePath(coursePath);

    return () => {
      setSelectedCoursePath(undefined);
    };
  }, [coursePath]);

  const hasKlassId = !isNaN(Number(klassId));

  const { data: klass } = useQuery({
    refetchOnWindowFocus: false,
    enabled: hasKlassId,
    ...klassesQueryKeys.get(Number(klassId)),
    staleTime: REQUEST_STALE_TIME_1_DAY_IN_MS,
  });

  const isContentCreatorUser = isContentCreator(authUser?.userType);

  const { status, slug, version } = coursePath || ({} as CoursePath);
  const courseAbbreviation = coursePath?.course.abbreviation ?? '';
  const courseTitle = `${courseAbbreviation} - ${t('courses')}`;
  const title = klass
    ? `${translateKlassName(klass.name)} - ${t('courses')}`
    : courseTitle;

  const closeModal = (needReload: boolean = true) => {
    setModalOpen('none');
    if (needReload) {
      setReload(!reload);
    }
  };

  const onSelectKlass = (klass: Klass) => {
    if (handleKlassChange(klass)) {
      navigate(`/courses/${klass.coursePathSlug}/class/${klass.id}`);
    }
  };
  const onDeselectKlass = () => navigate(`/courses/${slugCourseName}`);

  const checkEmptyActivities = async () => {
    const { results: versioningLessons } = await listLessonsByCourseSlug(
      slugCourseName,
    );
    const emptyActivities = await getEmptyActivities({ slugCourseName });

    const hasEmptyActivities = !!emptyActivities.length;
    const emptyActivitiesAux: EmptyActivityWithLesson[] = [];
    if (hasEmptyActivities) {
      versioningLessons?.forEach(versioningLesson => {
        const filterEmptyActivity = emptyActivities.filter(({ lesson }) =>
          lesson.includes(versioningLesson.id),
        );
        if (filterEmptyActivity && filterEmptyActivity.length) {
          filterEmptyActivity.map(activity =>
            emptyActivitiesAux.push({
              ...activity,
              lessonInfo: versioningLesson,
            }),
          );
        }
      });
      setEmptyActivities(emptyActivitiesAux);
    }
    setModalOpen(emptyActivitiesAux.length ? 'activity' : 'beta');
  };
  const filters: ListCoursePathFilters = {
    courseId: coursePath?.course.id ? [coursePath?.course.id] : undefined,
  };

  const requestDeleteActivities = async (
    emptyActivities: EmptyActivityWithLesson[],
  ) => {
    setIsLoadingDeleteActivities(true);
    const [request] = await Promise.allSettled(
      emptyActivities.map(({ lessonInfo: { id: lessonId }, id: activityId }) =>
        deleteActivity({
          activityId,
          lessonId,
        }),
      ),
    );

    if (request.status === 'rejected') {
      alert.error(tErrors('serverError'));
    } else {
      setIsLoadingDeleteActivities(false);
      setModalOpen('none');
    }
  };

  const allowedCourseSelectRoles = [
    UserTypeEnum.SUPER_ADMIN,
    UserTypeEnum.CONTENT_CREATOR,
    UserTypeEnum.UNIT_ADMIN,
    UserTypeEnum.TEACHER,
  ];

  function CourseStatusActionsHandler() {
    switch (status) {
      case VersioningStatusEnum.BETA:
        return <BetaActions />;
      case VersioningStatusEnum.EDITING:
        return <EditActions />;
      case VersioningStatusEnum.VERSIONING:
        return <VersioningActions />;
      case VersioningStatusEnum.PUBLISHED:
        return <PublishedActions />;
      default:
        return <div className="h-7 content-none" />;
    }
  }

  function PublishedActions() {
    return (
      <React.Fragment>
        <MainButton
          className="max-w-none md:max-w-none"
          onClick={handleVersioning}
          icon={<BranchIcon className="w-4 h-4" />}
          loading={activeRequest === 'version'}
          disabled={activeRequest !== 'none'}
          text={t('newVersion')}
          color="secondary"
        />
        <MainButton
          className="max-w-none md:max-w-none"
          onClick={handleEditingMode}
          disabled={activeRequest !== 'none'}
          loading={activeRequest === 'edit'}
          icon={<PencilIcon strokeWidth={1.4} />}
          text={t('editCourse')}
        />
      </React.Fragment>
    );
  }

  function EditActions() {
    return (
      <React.Fragment>
        <MainButton
          className="max-w-none md:max-w-none"
          onClick={() => setModalOpen('edit-publish')}
          icon={<ArrowUpTrayIcon className="w-4 h-4" />}
          disabled={activeRequest !== 'none'}
          text={t('publish')}
          color="secondary"
        />
        <MainButton
          dataTestId="backEditingButton"
          className="max-w-none md:max-w-none"
          onClick={handleEditingMode}
          disabled={activeRequest !== 'none'}
          loading={activeRequest === 'version'}
          icon={<PencilIcon strokeWidth={1.4} />}
          text={t('backEditing')}
        />
      </React.Fragment>
    );
  }

  function BetaActions() {
    return (
      <React.Fragment>
        <ConditionalWrapper
          condition={isContentCreatorUser}
          wrapper={<Tooltip text={tCommon('forbiddenAction')} />}
        >
          <MainButton
            dataTestId="publishButton"
            className="max-w-none md:max-w-none"
            onClick={() => setModalOpen('beta-publish')}
            icon={<ArrowUpTrayIcon className="w-4 h-4" />}
            disabled={activeRequest !== 'none' || isContentCreatorUser}
            text={t('publish')}
            color="secondary"
          />
        </ConditionalWrapper>
        <MainButton
          dataTestId="enableEditingButton"
          className="max-w-none md:max-w-none"
          onClick={() => setModalOpen('edit')}
          disabled={activeRequest !== 'none'}
          loading={activeRequest === 'version'}
          icon={<PencilIcon strokeWidth={1.4} />}
          text={t('enableEditing')}
        />
      </React.Fragment>
    );
  }

  function VersioningActions() {
    return (
      <React.Fragment>
        <MainButton
          className="max-w-none md:max-w-none"
          onClick={() => checkEmptyActivities()}
          icon={<ArrowUpTrayIcon className="w-4 h-4" />}
          loading={activeRequest === 'version'}
          disabled={activeRequest !== 'none'}
          text={t('finish')}
          color="secondary"
        />
        <MainButton
          dataTestId="backEditingButton"
          className="max-w-none md:max-w-none"
          onClick={handleVersioning}
          disabled={activeRequest !== 'none'}
          loading={activeRequest === 'version'}
          icon={<PencilIcon strokeWidth={1.4} />}
          text={t('backEditing')}
        />
      </React.Fragment>
    );
  }

  async function handleVersioning() {
    try {
      setActiveRequest('version');
      if (!coursePath) throw new Error('Course path not found');
      await allowVersioningMode(coursePath, version <= 1.0);
    } catch (error: any) {
      const api = new ApiError(error);
      alert.error(api.getErrorMessage());
    } finally {
      setActiveRequest('none');
    }
  }
  async function handleEditingMode() {
    try {
      setActiveRequest('edit');
      await allowEditingMode(coursePath!);
    } catch (error: any) {
      const api = new ApiError(error);
      alert.error(api.getErrorMessage());
    } finally {
      setActiveRequest('none');
    }
  }

  async function publishBeta() {
    if (!coursePath) return;
    await courseToPublish(coursePath.slug);
  }

  if (!coursePath && isError) {
    return (
      <ErrorComponent
        errorTextSubTitle={t('errorTextSubTitle')}
        errorTextTitle={t('errorTextTitle')}
      />
    );
  }

  return (
    <Layout className="gap-0">
      <HeadTitle routeInfo={title} />
      <div className="flex items-center gap-2.5">
        <Link
          to="/courses"
          className="hover:opacity-75 transition-all ease-in-out duration-150"
        >
          <ChevronLeftIcon className="w-8 h-8 text-primary" />
        </Link>
        <Heading3 text={coursePath?.course.name} className="text-primary" />
      </div>

      <PublishEditModal
        onClose={closeModal}
        slug={slug}
        visible={modalOpen === 'edit-publish'}
      />
      <EnableEditModal
        course={coursePath!}
        onClose={() => closeModal(false)}
        visible={modalOpen === 'edit'}
      />
      <PublishBetaModal
        onConfirm={publishBeta}
        onClose={closeModal}
        visible={modalOpen === 'beta-publish'}
      />
      <BetaModal
        onClose={closeModal}
        slug={slug}
        visible={modalOpen === 'beta'}
      />

      <div className="flex flex-col gap-4">
        <div
          className={twJoin(
            'gap-4 pt-4',
            allowedCourseSelectRoles.includes(
              authUser?.userType || UserTypeEnum.PARENT,
            ) && 'grid md:grid-cols-2',
          )}
        >
          <ComponentGuard roles={allowedCourseSelectRoles}>
            <InfiniteSearchInput
              selectedItem={selectedCoursePath}
              service={coursePathsQueryKeys.list}
              displayName={coursePath =>
                `${coursePath.course.name} v${coursePath.version}`
              }
              onSelect={coursePath => {
                navigate(`/courses/${coursePath.slug}`);
                setSelectedCoursePath(coursePath);
              }}
              filters={filters}
              options={{ enabled: !!coursePath }}
              onDeselect={() => setSelectedCoursePath(undefined)}
              input={{ className: { base: 'md:w-96 font-500' } }}
            />
          </ComponentGuard>

          <div className="flex items-center justify-end gap-4">
            <ComponentGuard
              roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.CONTENT_CREATOR]}
            >
              {coursePath && <CourseStatus version={version} status={status} />}
              <CourseStatusActionsHandler />
            </ComponentGuard>
            <ComponentGuard
              roles={[UserTypeEnum.UNIT_ADMIN, UserTypeEnum.TEACHER]}
            >
              <ExtraLessonsOptions
                klass={klass}
                hasInvalidLessons={hasInvalidLessons}
                setChanged={setChanged}
              />

              <InfiniteSearchInput
                service={klassesQueryKeys.list}
                filters={{
                  unitId: getAuthorizedUnits(authUser),
                  status: isUnitStaff(authUser?.userType)
                    ? [KlassStatusEnum.IN_PROGRESS, KlassStatusEnum.OPEN]
                    : undefined,
                }}
                displayName={klass => translateKlassName(klass.name)}
                selectedItem={klass}
                onSelect={onSelectKlass}
                onDeselect={onDeselectKlass}
                input={{
                  className: { base: 'w-full md:w-72 lg:w-96 font-500' },
                }}
              />
            </ComponentGuard>
          </div>
        </div>

        <ListLessonsFactory
          klassId={Number(klassId) || undefined}
          slugCourseName={slugCourseName}
          onInvalidLessons={setHasInvalidLessons}
        />
        <LegalInformationCourseCollapse course={coursePath} isViewMode />
      </div>

      <ModalEmptyActivity
        isRequesting={isLoadingDeleteActivities}
        isVisible={modalOpen === 'activity'}
        emptyActivities={emptyActivities}
        onClickConfirm={async () => {
          setIsLoadingDeleteActivities(true);
          await requestDeleteActivities(emptyActivities);
          setModalOpen('beta');
        }}
        onClickCancel={closeModal}
      />
    </Layout>
  );
}

type ExtraLessonsOptionsProps = {
  klass: Klass | undefined;
  hasInvalidLessons?: boolean;
  setChanged: (changed: boolean) => void;
};

function ExtraLessonsOptions({
  klass,
  hasInvalidLessons,
  setChanged,
}: ExtraLessonsOptionsProps) {
  const { user } = useAuth();
  const klassId = klass?.id || 0;
  const hasKlassId = Boolean(klassId);
  const isUserUnitAdmin = isUnitAdmin(user?.userType);

  const { t } = useTranslation('translation', {
    keyPrefix: 'lesson.manage',
  });

  const [modalOpen, setModalOpen] = useState<'extraLesson' | 'lessonBank'>();
  const [lesson, setLesson] = useState<Lesson>();
  const navigate = useNavigate();
  const goToKlassLessons = () => navigate(`/class/${klassId}/lessons`);
  const closeModal = () => setModalOpen(undefined);
  const onUse = async (lesson: Lesson) => {
    setLesson(lesson);
    setModalOpen('extraLesson');
  };

  const queryClient = useQueryClient();
  const queryKey = scheduledLessonsQueryKeys.list({ klassId }).queryKey;

  const updateScheduledLessons = async () => {
    setModalOpen(undefined);
    await queryClient.invalidateQueries(queryKey);
    goToKlassLessons();
    setChanged(true);
  };

  function ActionCards() {
    return (
      <Fragment>
        <ConditionalRenderer condition={hasKlassId}>
          <div className="flex items-start flex-col gap-1.5">
            <ActionDetailCard
              title={t('addLesson.title')}
              details={t('addLesson.details')}
              icon={PlusIcon}
              onClick={() => setModalOpen('extraLesson')}
            />
            <ActionDetailCard
              onClick={() => setModalOpen('lessonBank')}
              title={t('bankLesson.title')}
              details={t('bankLesson.details')}
              icon={RectangleStackIcon}
            />
            <ActionDetailCard
              onClick={goToKlassLessons}
              title={t('editLesson.title')}
              details={t('editLesson.details')}
              icon={PencilIcon}
            />
          </div>
        </ConditionalRenderer>
        <ConditionalRenderer condition={!hasKlassId}>
          <Text
            testId="tooltipalert"
            text={t('selectKlass')}
            className="text-primary"
          />
        </ConditionalRenderer>
      </Fragment>
    );
  }

  if (!isUserUnitAdmin && klass?.teacherId !== user?.id) {
    return null;
  }

  return (
    <Fragment>
      <ConditionalRenderer condition={klassId}>
        {klassId && (
          <Fragment>
            <AddExtraLessonModal
              isVisible={modalOpen === 'extraLesson'}
              onCancel={closeModal}
              klassId={klassId}
              lesson={lesson}
              updateScheduledLessons={updateScheduledLessons}
            />
            <ModalBank
              visible={modalOpen === 'lessonBank'}
              initial="lessons"
              onUseLesson={onUse}
              onClose={closeModal}
            />
          </Fragment>
        )}
      </ConditionalRenderer>
      <Tooltip
        color="white"
        classNameContainer="flex items-center justify-center h-full"
        options={{
          placement: 'auto',
          interactive: true,
          delayHide: 500,
        }}
        text={
          <ConditionalRenderer
            condition={!hasInvalidLessons}
            fallback={
              <Text
                testId="invalidLessonsMessage"
                text={t('invalidLessonsMessage')}
                className="text-error"
              />
            }
          >
            <ActionCards />
          </ConditionalRenderer>
        }
      >
        <CogIcon
          data-testid="engineIcon"
          className={twJoin(
            'w-4 h-4 text-primary',
            !hasKlassId && 'opacity-20',
            hasInvalidLessons && 'text-red-500 disabled',
          )}
        />
      </Tooltip>
    </Fragment>
  );
}
